// layout/header.ts — role-aware site header.
//
// Renders into <header id="site-header"> and re-renders whenever
// onUserChange fires (sign-in, sign-out, or boot validation finishing).

import { html, markGlyph, initialsOf } from '../util';
import { getUser, getUserRole, onUserChange, logout } from '../auth';
import { navigate, parseHash } from '../router';

interface NavItem { id: string; label: string }

function buildItems(): NavItem[] {
  const user = getUser();
  const role = getUserRole();
  if (!user) {
    return [
      { id: 'landing',       label: 'Home' },
      { id: 'about',         label: 'About' },
      { id: 'grant-info',    label: 'Apply' },
      { id: 'public-grants', label: 'Grants' },
    ];
  }
  if (role === 'user') {
    return [
      { id: 'user-dashboard', label: 'Dashboard' },
      { id: 'about',          label: 'About' },
    ];
  }
  if (role === 'approver') {
    return [
      { id: 'admin-dashboard', label: 'Dashboard' },
      { id: 'about',           label: 'About' },
      { id: 'payout',          label: 'Funding target' },
    ];
  }
  if (role === 'admin') {
    return [
      { id: 'admin-dashboard', label: 'Dashboard' },
      { id: 'user-management', label: 'Users' },
      { id: 'about',           label: 'About' },
      { id: 'payout',          label: 'Funding target' },
    ];
  }
  return [];
}

function renderHeaderInner(): string {
  const user = getUser();
  const items = buildItems();
  const { name: currentRoute } = parseHash(location.hash);

  const navItems = items.map(it => html`
    <a class="nav-link${it.id === currentRoute ? ' is-active' : ''}"
       href="#/${it.id}" data-nav="${it.id}">${it.label}</a>
  `).join('');

  const right = user
    ? html`
        <a class="nav-link" href="#" data-action="logout">Sign out</a>
        <div class="avatar" title="${user.FullName || user.fullName || ''}">${initialsOf(user.FullName || user.fullName)}</div>
      `
    : html`
        <a class="nav-link" href="#/login" data-nav="login">Log in</a>
        <a class="nav-link" href="#/signup" data-nav="signup">Sign up</a>
        <a class="btn btn-primary ms-2" href="#/grant-info" data-nav="grant-info">Apply for a grant</a>
      `;

  return html`
    <div class="wrap">
      <a class="brand" href="#/landing" data-nav="landing">
        ${{ __raw: markGlyph(38) }}
        <span class="d-flex flex-column">
          <span class="brand-name">The Bray Family Foundation</span>
          <span class="brand-sub">Est. 1995 · 501(c)(3)</span>
        </span>
      </a>
      <nav class="nav-bar">
        ${{ __raw: navItems }}
        <span class="nav-divider"></span>
        ${{ __raw: right }}
      </nav>
    </div>
  `;
}

/** One-time setup: writes initial markup, wires delegated listeners,
 *  and subscribes to user changes to re-render. */
export function mountHeader(rootId = 'site-header'): void {
  const root = document.getElementById(rootId);
  if (!root) return;

  const rerender = () => { root.innerHTML = renderHeaderInner(); };
  rerender();
  onUserChange(rerender);
  window.addEventListener('hashchange', rerender);

  // Event delegation — single listener on the header.
  root.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const navEl = target.closest('[data-nav]') as HTMLElement | null;
    if (navEl) {
      e.preventDefault();
      navigate(navEl.getAttribute('data-nav')!);
      return;
    }
    const actionEl = target.closest('[data-action]') as HTMLElement | null;
    if (actionEl) {
      e.preventDefault();
      const action = actionEl.getAttribute('data-action');
      if (action === 'logout') {
        void logout().then(() => navigate('landing'));
      }
    }
  });
}

/** Toggle the chrome (header + footer) for full-screen routes. */
export function setChromeVisible(visible: boolean): void {
  const h = document.getElementById('site-header');
  const f = document.getElementById('site-footer');
  if (h) h.style.display = visible ? '' : 'none';
  if (f) f.style.display = visible ? '' : 'none';
}
